<template>
  <b-card no-body
          :v-draggable="isLegacyMode"
          ref="action_management_center"
          :id="isLegacyMode ? 'action-management-center' : 'action-management-center-free'"
          :class="{ spinner: isLoading, 'local-card': !isLegacyMode }">
    <!-- Header -->
    <b-card-header  v-if="isLegacyMode"
                    style="color: lightgrey; height: 25px; cursor: move"
                    class="pl-2 pb-0 pt-0"
                    header-tag="div"
                    header-bg-variant="dark">
      <b-row class="mx-0 px-0">
        <b-col class="mx-0 px-0" cols="11">
          {{ $t('Webportal.CX.Dashboard.ActionManagement') }}
        </b-col>
        <b-col class="mx-0 px-0" cols="1" style="text-align: right">
          <font-awesome-icon  class="mx-1 clickable"
                              :icon="['far', 'times']"
                              @click="$emit('close')" />
        </b-col>
      </b-row>
    </b-card-header>

    <!-- Content -->
    <b-card-text  style="overflow: auto">
      <b-table hover :items="Items" :fields="TableFields" v-if="HasItems" class="table-text">
        <template #cell(Source)="data">
          <template v-if="data.value.isImg">
            <img :src="data.value.src" class="source-logo" :style="{ 'background-color': data.value.bg_color }" />
          </template>
          <span v-else>
            <IntermediaryImage :OverrideText="data.value.src" />
          </span>
        </template>

        <template #cell(ActNow)="data">
          <font-awesome-icon
            :class="{ disabled: isResolved(data.item) }"
            class="mx-1 clickable action-button"
            :icon="['fas', 'file-' + data.item.Action]"
            @click="showSurvey(data.item)"
            v-if="data.item.Action"
          />
          <span v-else>-</span>
        </template>
      </b-table>
      <span v-else>
        {{ $t("Webportal.CX.Dashboard.ActionManagementCenter.NoActionToTake") }}
      </span>
    </b-card-text>

    <ActionManagementCenterSurvey
      :actionToTake="surveyActionToTake"
      :firstAidKit="currentFirstAidKit"
      v-if="ShowSurvey"
      @close="checkSurvey"
      class="resolve-survey-popup"
    />
  </b-card>
</template>

<script>
import ActionManagementCenterSurvey from "./ActionManagementCenter_Survey";
import IntermediaryImage from "@/components/CX/ExplorationCard/IntermediaryImage";
import DashboardService from "@/services/DashboardService";
import CancelToken from '@/mixins/CancelToken';
import EventBus from "@/services/EventBus";

export default {
  name: "ActionManagementCenterPopup",

  components: {
    ActionManagementCenterSurvey,
    IntermediaryImage
  },

  mixins: [ CancelToken ],

  props: {
    clientLocationId:           { type: Number },
    explorationAugmentionData:  { type: Array,    default: () => [], },
    explorationData:            { type: Array,    default: () => [], },
    isLegacyMode:               { type: Boolean,  default: true },
    resolvedApiIds:             { type: Array,    default: () => [] },
    resolvedExtSurveyIds:       { type: Array,    default: () => [] },
    threshold:                  { type: Array, required: true }
  },

  data() {
    return {
      isLoading: false,
      cancelToken: null,

      surveyActionToTake: null,
      currentFirstAidKit: null
    };
  },

  watch: {},

  computed: {
    ShowSurvey: function() {
      return !_.isEmpty(this.surveyActionToTake) && !_.isEmpty(this.currentFirstAidKit);
    },
    HasItems: function() {
      return this.Items?.length > 0;
    },
    Items: function() {
      return (this.MappedExplorations || []).concat(this.MappedExplorationAugmentions || []);
    },
    MappedExplorationAugmentions: function() {
      return this.explorationAugmentionData
        .filter(this.explorationAugmentionFilter)
        .map(ea =>
          ({
            // text = Override, imgId = Upload, img = Mapped
            Source:       { text: ea.OverrideText, title: ea.SurveyTitle, imgId: ea.ExtLogoId, img: ea.LogoUrl },
            Score:        ea.Result + '%',
            Target:       ea.Threshold + '%',
            Appeared:     ea.WaveDate || NaN,
            Action:       this.hasFirstAidKit(ea) ? 'pdf' : null,
            Status:       this.explorationAugmentionIsResolved(ea) ? 'Completed' : 'Open',
            _RAW: ea,
            IsApi: false,
            Id: ea.ExternalSurveyId,
            FirstAidKit: ea.FirstAidKit
          })
        );
    },
    MappedExplorations: function() {
      // APIs mapped to table format
      return this.explorationData
        .filter(this.explorationFilter)
        .map(e =>
          ({
            Source:       { img: e.PictureURL, imgBg: e.PictureURL === 'img/seobility-logo.svg' ? '#327BB4' : undefined },
            Score:        `${e.Threshold?.Value}${this.getValueLimitter(e.Threshold?.MaxRating)}`,
            Target:       `${e.Threshold?.Threshold}${this.getValueLimitter(e.Threshold?.MaxRating)}`,
            Appeared:     e.Threshold?.Date,
            Action:       this.hasFirstAidKit(e) ? 'pdf' : null,
            Status:       this.explorationIsResolved(e) ? 'Completed' : 'Open',
            _RAW: e,
            IsApi: true,
            Id: e.ApiID,
            FirstAidKit: e.FirstAidKit
          })
        );
    },
    TableFields: function() {
      const prefix = 'Webportal.CX.Dashboard.ActionManagementCenter.';
      return [
        { key: 'Source',    label: this.$t(prefix + 'Source'),    formatter: 'sourceFormatter' },
        { key: 'Score',     label: this.$t(prefix + 'Score'),     tdAttr:    this.getTableColor },
        { key: 'Target',    label: this.$t(prefix + 'Target'),    tdAttr:    this.getTableColor },
        { key: 'Appeared',  label: this.$t(prefix + 'Appeared'),  formatter: 'dateFormatter' },
        // 'Antwort-Zeit' : 'Response time' || 'Kunden-Rückmeldungen' : 'Customer Feedback'
        // { key: 'Improve',   label: this.$t(prefix + 'Improve') },
        { key: 'Action',    label: this.$t(prefix + 'Action'),    formatter: 'firstAidKitLogoFormatter' },
        { key: 'ActNow',    label: this.$t(prefix + 'ActNow') },
        { key: 'Status',    label: this.$t(prefix + 'Status'),    formatter: 'translationFormatter' }
      ];
    },
  },

  mounted: function () {},
  methods: {
    firstAidKitLogoFormatter(value, key, item) {
      switch (value) {
        case 'pdf': return this.translationFormatter('PdfManual', key, item);
        case 'avi': return this.translationFormatter('VideoManual', key, item);
        default:    return this.translationFormatter('-', key, item);
      }
    },
    translationFormatter(value, key, item) {
      return (value || '-') === '-' ? '-' : this.$t('Webportal.CX.Dashboard.ActionManagementCenter.' + value);
    },

    sourceFormatter(value, key, item) {
      // Text first, as it overrides the logo
      if (value?.text)
        return { isImg: false, src: value.text };
      if (value?.imgId)
        return { isImg: true, src: `${process.env.BASE_URL}api/media/image?id=${value.imgId}` };
      if (value?.img)
        return { isImg: true, src: (value.img.includes('static') ? `${process.env.BASE_URL}${value.img}` : `${process.env.BASE_URL}static/${value.img}`), bg_color: value.imgBg };
      if (value?.title)
        return { isImg: false, src: value.title };

      // Show 'N / A', as used in ExplorationSubCard
      return { isImg: false, src: this.$t('Webportal.CX.Dashboard.Exploration.NoData') };
    },
    dateFormatter(value, key, item) {
      const date = new Date(value || undefined);
      return isNaN(date) ? this.$t('Webportal.CX.Dashboard.Exploration.NoData') : date.toLocaleDateString();
    },
    getValueLimitter(maxRating) {
      return maxRating == 100 ? '%' : '';
    },

    explorationFilter(e) {
      return e != null && (
        (!e.Inactive && !e.Threshold?.IsAccomplished) ||
        (this.explorationIsResolved(e))
      );
    },
    explorationAugmentionFilter(e) {
      return e != null && (
        (!e.IsAccomplished) ||
        (this.explorationAugmentionIsResolved(e))
      );
    },

    hasFirstAidKit(e) {
      return !_.isEmpty(e.FirstAidKit);
    },
    isActionToTake(e) {
      return this.hasFirstAidKit(e) && !this.isResolved(e);
    },
    isResolved(e) {
      return this.explorationIsResolved(e._RAW) || this.explorationAugmentionIsResolved(e._RAW);
    },
    explorationIsResolved(e) {
      return this.resolvedApiIds.some(i => i.ApiID == e.ApiID);
    },
    explorationAugmentionIsResolved(e) {
      return this.resolvedExtSurveyIds.some(i => i.ExternalSurveyId == e.ExternalSurveyId);
    },

    checkSurvey(att) {
      // Regular close
      if (att == null) {
        // Close survey popup and unlock ACM
        this.surveyActionToTake = null;
        this.currentFirstAidKit = null;
        this.isLoading = false;
        return console.warn('AMC survey was closed by user.');
      }

      // Success
      this.isLoading = true;
      this.cancelToken = this.initCancelToken(this.cancelToken);

      // Resolve first aid kit for this client location
      DashboardService.setFirstAidKitAsResolved(
          {
            clientLocationId:   this.clientLocationId,
            firstAidKitGroupId: this.currentFirstAidKit.FirstAidKit
          },
          this.cancelToken
        )
        .then(() => {
          // Success - change item in list
          // _RAW is a reference to the item upto the dashboard
          // As we have to set all elements as resolved, that use this first aid kit,
          // we manage the Apis(FromSurveys) in dashboard

          this.$emit('resolved', this.currentFirstAidKit.FirstAidKit);
          EventBus.$emit("Resolved_FirstAidKit", this.currentFirstAidKit.FirstAidKit);
        })
        .catch(error => console.error('Failed to mark action as resolved.', error))
        .finally(() => {
          // Close survey popup and unlock ACM
          this.surveyActionToTake = null;
          this.currentFirstAidKit = null;
          this.cancelToken = null;
          this.isLoading = false;
        })
    },

    getRandom(min, max) {
      min = Math.ceil(min);
      max = Math.floor(max);
      return Math.floor(Math.random() * (max - min + 1) + min);
    },

    showSurvey(att) {
      // Prevent opening on resolved items
      if (this.isResolved(att))
        return;

      // Lock AMC until the survey was closed
      this.isLoading = true;
      this.surveyActionToTake = att;
      this.currentFirstAidKit = att.FirstAidKit;
    },

    getTableColor(value, key, item) {
      let color = this.getColor((key == "Score") ? 0 : 100);
      return { style: 'font-weight: bold; color: ' + color };
    },

    getColor(value) {
      // Too lazy..
      if (!this.threshold) return null;

      return this.threshold.find(
        (thres) => thres.Minimum <= value && thres.Maximum >= value
      )?.Color;
    }
  },
};
</script>

<style scoped>
.local-card {
  border-color: transparent!important;
}
.action-button {
  font-size: 25px;
}
.disabled {
  cursor: not-allowed !important;
  color: gray !important;
}
.source-logo {
  max-height: 50px;
  max-width: 150px;
}
.resolve-survey-popup {
  width: 1400px;
  height: 800px;
  top: 0px;
  left: -50px;
  position: relative;
  border: none;
  box-shadow: 3px 3px 13px -3px var(--border-color-secondary);
}
.table ::v-deep .table-text,
.table ::v-deep .table-text *,
.table ::v-deep .table th, .table td {
  text-align: center;
  vertical-align: middle !important;
}
</style>
